import type { IOrdinalsStrayFunds, OrdinalsStrayFundsErrorCode } from 'api/api'

// How long a finished sweep keeps its confirmation on screen.
export const STRAY_FUNDS_MOVED_NOTICE_MS = 15 * 60 * 1000
export const STRAY_FUNDS_POLL_MS = 2500

export type OrdinalsStrayFundsView =
    | { kind: 'hidden' }
    | {
          kind: 'offer'
          satoshis: number
          errorCode: OrdinalsStrayFundsErrorCode | null
          signsOnDevice: boolean
      }
    | { kind: 'moving'; satoshis: number }
    | { kind: 'moved'; satoshis: number; txid: string }

const finishedRecently = (finishedAt: string | null, nowMs: number) => {
    const finishedMs = finishedAt ? Date.parse(finishedAt) : NaN
    return Number.isFinite(finishedMs) && nowMs - finishedMs < STRAY_FUNDS_MOVED_NOTICE_MS
}

// A running sweep wins; otherwise anything still at the address is offered
// (with the last failure, if any), and a recent success is confirmed once the
// address is empty. Device-signed wallets get the same offer; the device signs
// the move when the user taps it. Funds no wallet can sign for are not shown.
export function ordinalsStrayFundsView(
    summary: IOrdinalsStrayFunds | undefined,
    nowMs: number,
): OrdinalsStrayFundsView {
    if (!summary?.ok) return { kind: 'hidden' }

    const recovery = summary.recovery
    if (recovery?.status === 'in_progress') return { kind: 'moving', satoshis: summary.satoshis }

    if (summary.satoshis > 0) {
        if (!summary.recoverable) return { kind: 'hidden' }
        return {
            kind: 'offer',
            satoshis: summary.satoshis,
            errorCode: recovery?.status === 'failed' ? recovery.error_code : null,
            signsOnDevice: summary.signing === 'device',
        }
    }

    if (
        recovery?.status === 'succeeded' &&
        recovery.txid &&
        finishedRecently(recovery.finished_at, nowMs)
    ) {
        return { kind: 'moved', satoshis: recovery.satoshis || 0, txid: recovery.txid }
    }

    return { kind: 'hidden' }
}

export function ordinalsStrayFundsPollInterval(summary: IOrdinalsStrayFunds | undefined) {
    return summary?.recovery?.status === 'in_progress' ? STRAY_FUNDS_POLL_MS : false
}

export function ordinalsStrayFundsErrorMessage(code: OrdinalsStrayFundsErrorCode | null) {
    switch (code) {
        case 'unconfirmed':
            return 'This deposit hasn’t confirmed yet. Try again once it has, usually within 10 minutes.'
        case 'nothing_to_move':
            return 'These funds couldn’t be verified yet. Try again in a few minutes.'
        case 'timed_out':
            return 'The last attempt took too long. Try again.'
        case 'sweep_failed':
            return 'The last attempt didn’t go through. Try again.'
        default:
            return null
    }
}
